import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Check, Circle, ChevronDown, ChevronRight,
  StickyNote, Users, Plus, X, CalendarDays, UserPlus, AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAppContext } from "@/lib/onboardingContext";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/lib/i18nContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Note {
  id: string;
  stepIndex: number;
  text: string;
  createdAt: Date;
}

interface TaskAssignment {
  id: string;
  stepIndex: number;
  substepIndex: number;
  assignee: string;
  status: "todo" | "in-progress" | "done";
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
}

export default function Timeline() {
  const navigate = useNavigate();
  const { results: rawResults, ensureResults } = useAppContext();
  const results = rawResults ?? ensureResults();
  const { t } = useI18n();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [notes, setNotes] = useState<Note[]>([]);
  const [assignments, setAssignments] = useState<TaskAssignment[]>([]);
  const [newNoteStep, setNewNoteStep] = useState<number | null>(null);
  const [newNoteText, setNewNoteText] = useState("");
  const [assignModal, setAssignModal] = useState<{ step: number; sub: number } | null>(null);
  const [calMonth, setCalMonth] = useState(() => new Date().getMonth());
  const [calYear, setCalYear] = useState(() => new Date().getFullYear());
  
  // Dynamic team members
  const [teamMembers, setTeamMembers] = useState<{ id: string; name: string }[]>([]);
  const [newMemberName, setNewMemberName] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);

  // Load team members from database
  useEffect(() => {
    const loadTeamMembers = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("team_members")
        .select("id, name")
        .order("created_at", { ascending: true });
      if (data) setTeamMembers(data);
    };
    loadTeamMembers();
  }, []);

  const addTeamMember = useCallback(async () => {
    const trimmed = newMemberName.trim();
    if (!trimmed) return;
    if (teamMembers.some(m => m.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error("Team member already exists");
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Please sign in to add team members"); return; }
    const { data, error } = await supabase
      .from("team_members")
      .insert({ user_id: user.id, invited_by_user_id: user.id, name: trimmed })
      .select("id, name")
      .single();
    if (error) { toast.error("Failed to add team member"); return; }
    if (data) setTeamMembers(prev => [...prev, data]);
    setNewMemberName("");
    setShowAddMember(false);
  }, [newMemberName, teamMembers]);

  const removeTeamMember = useCallback(async (id: string) => {
    const { error } = await supabase.from("team_members").delete().eq("id", id);
    if (error) { toast.error("Failed to remove team member"); return; }
    setTeamMembers(prev => prev.filter(m => m.id !== id));
    setAssignments(prev => prev.filter(a => {
      const member = teamMembers.find(m => m.id === id);
      return !member || a.assignee !== member.name;
    }));
  }, [teamMembers]);

  // Build calendar tasks from timeline steps spread across weeks
  const calendarTasks = useMemo(() => {
    if (!results) return {};
    const tasks: Record<string, { label: string; color: string }[]> = {};
    const today = new Date();
    results.timelineSteps.forEach((s, i) => {
      const weekMatch = s.duration.match(/(\d+)/);
      const weeks = weekMatch ? parseInt(weekMatch[1]) : 1;
      const startDate = new Date(today);
      const priorWeeks = results.timelineSteps.slice(0, i).reduce((sum, ps) => {
        const m = ps.duration.match(/(\d+)/);
        return sum + (m ? parseInt(m[1]) : 1);
      }, 0);
      startDate.setDate(startDate.getDate() + priorWeeks * 7);
      for (let w = 0; w < weeks; w++) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + w * 7);
        const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        if (!tasks[key]) tasks[key] = [];
        tasks[key].push({
          label: s.step.length > 18 ? s.step.slice(0, 18) + "…" : s.step,
          color: s.done ? "bg-primary/20 text-primary" : "bg-accent/20 text-accent-foreground",
        });
      }
    });
    return tasks;
  }, [results]);

  // (results is always non-null thanks to ensureResults() above; we no
  // longer bounce paid users back to /onboarding from this page.)

  const toggle = (i: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const addNote = () => {
    if (newNoteStep === null || !newNoteText.trim()) return;
    setNotes(prev => [...prev, {
      id: crypto.randomUUID(),
      stepIndex: newNoteStep,
      text: newNoteText.trim(),
      createdAt: new Date(),
    }]);
    setNewNoteText("");
    setNewNoteStep(null);
  };

  const removeNote = (id: string) => setNotes(prev => prev.filter(n => n.id !== id));

  const assignTask = (assignee: string) => {
    if (!assignModal) return;
    setAssignments(prev => [...prev, {
      id: crypto.randomUUID(),
      stepIndex: assignModal.step,
      substepIndex: assignModal.sub,
      assignee,
      status: "todo",
    }]);
    setAssignModal(null);
  };

  const cycleStatus = (id: string) => {
    setAssignments(prev => prev.map(a => {
      if (a.id !== id) return a;
      const next = a.status === "todo" ? "in-progress" : a.status === "in-progress" ? "done" : "todo";
      return { ...a, status: next };
    }));
  };

  const removeAssignment = (id: string) => setAssignments(prev => prev.filter(a => a.id !== id));



  const calDays = getCalendarDays(calYear, calMonth);

  const statusColors: Record<string, string> = {
    "todo": "bg-muted text-muted-foreground",
    "in-progress": "bg-accent/20 text-accent-foreground",
    "done": "bg-primary/20 text-primary",
  };

  const { onboarding } = useAppContext();
  const hasFullLiquor = onboarding.alcoholType === "full-liquor" || onboarding.businessType?.includes("bar");

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-medium text-foreground">{t("timeline.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("timeline.estimated", { time: results.timeline })}</p>
          </div>
        </div>
      </div>

      <div className="container pt-8">
        {/* Liquor license warning banner */}
        {hasFullLiquor && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">{t("timeline.liquorWarning")}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("timeline.liquorDetail")}</p>
            </div>
          </motion.div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* LEFT: Timeline with inline notes */}
          <div className="lg:col-span-2 space-y-8">
            {/* Timeline steps */}
            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-border" />
              {results.timelineSteps.map((s, i) => {
                const isOpen = expanded.has(i);
                const stepNotes = notes.filter(n => n.stepIndex === i);
                const stepAssignments = assignments.filter(a => a.stepIndex === i);
                return (
                  <motion.div
                    key={s.step}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className="relative pb-6 last:pb-0"
                  >
                    <button
                      onClick={() => toggle(i)}
                      className="flex items-start gap-4 w-full text-left group"
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 shrink-0 transition-colors ${
                        s.done ? "bg-primary" : "bg-card border-2 border-border group-hover:border-primary/50"
                      }`}>
                        {s.done ? <Check className="w-4 h-4 text-primary-foreground" /> : <Circle className="w-3 h-3 text-muted-foreground" />}
                      </div>
                      <div className="pt-2 flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className={`font-medium ${s.done ? "text-primary" : "text-foreground"}`}>{s.step}</h3>
                          <div className="flex items-center gap-2">
                            {stepNotes.length > 0 && (
                              <Badge variant="secondary" className="text-xs gap-1">
                                <StickyNote className="w-3 h-3" /> {stepNotes.length}
                              </Badge>
                            )}
                            {stepAssignments.length > 0 && (
                              <Badge variant="secondary" className="text-xs gap-1">
                                <Users className="w-3 h-3" /> {stepAssignments.length}
                              </Badge>
                            )}
                            {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{s.duration}</p>
                      </div>
                    </button>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden ml-14"
                        >
                          <div className="pt-3 space-y-3">
                            {/* Substeps with assign button */}
                            {s.substeps.map((sub, j) => {
                              const subAssignments = stepAssignments.filter(a => a.substepIndex === j);
                              return (
                                <motion.div
                                  key={j}
                                  initial={{ opacity: 0, y: 5 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: j * 0.05 }}
                                  className="bg-card rounded-lg p-3 border border-border"
                                >
                                  <div className="flex items-start gap-2">
                                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                      <span className="text-xs font-medium text-primary">{j + 1}</span>
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-medium text-foreground">{sub.task}</h4>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
                                          onClick={(e) => { e.stopPropagation(); setAssignModal({ step: i, sub: j }); }}
                                        >
                                          <UserPlus className="w-3 h-3 mr-1" /> {t("timeline.assign")}
                                        </Button>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{sub.detail}</p>
                                      {/* Assigned people */}
                                      {subAssignments.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                          {subAssignments.map(a => (
                                            <button
                                              key={a.id}
                                              onClick={(e) => { e.stopPropagation(); cycleStatus(a.id); }}
                                              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full cursor-pointer transition-colors ${statusColors[a.status]}`}
                                              title="Click to cycle status"
                                            >
                                              {a.assignee}
                                              <span className="opacity-60">• {a.status}</span>
                                              <X
                                                className="w-3 h-3 ml-0.5 opacity-40 hover:opacity-100"
                                                onClick={(e) => { e.stopPropagation(); removeAssignment(a.id); }}
                                              />
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}

                            {/* Notes for this step */}
                            {stepNotes.length > 0 && (
                              <div className="space-y-2 pt-2 border-t border-border/50">
                                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                  <StickyNote className="w-3 h-3" /> {t("timeline.notes")}
                                </p>
                                {stepNotes.map(n => (
                                  <div key={n.id} className="bg-secondary/50 rounded-lg p-2.5 text-sm text-foreground flex items-start gap-2">
                                    <span className="flex-1">{n.text}</span>
                                    <button onClick={() => removeNote(n.id)} className="text-muted-foreground hover:text-destructive shrink-0">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Add note inline */}
                            {newNoteStep === i ? (
                              <div className="flex gap-2 pt-1">
                                <Textarea
                                  value={newNoteText}
                                  onChange={e => setNewNoteText(e.target.value)}
                                  placeholder={t("timeline.addNotePlaceholder")}
                                  className="text-sm min-h-[60px]"
                                  autoFocus
                                />
                                <div className="flex flex-col gap-1">
                                   <Button size="sm" onClick={addNote} disabled={!newNoteText.trim()}>{t("timeline.save")}</Button>
                                   <Button size="sm" variant="ghost" onClick={() => { setNewNoteStep(null); setNewNoteText(""); }}>{t("timeline.cancel")}</Button>
                                </div>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-muted-foreground hover:text-primary"
                                onClick={() => setNewNoteStep(i)}
                              >
                                <Plus className="w-3 h-3 mr-1" /> {t("timeline.addNote")}
                              </Button>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            {/* Calendar */}
            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg font-medium text-foreground flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-primary" /> {t("timeline.taskCalendar")}
                </h2>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => {
                    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
                    else setCalMonth(m => m - 1);
                  }}>←</Button>
                  <span className="text-sm font-medium min-w-[140px] text-center">{MONTHS[calMonth]} {calYear}</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
                    else setCalMonth(m => m + 1);
                  }}>→</Button>
                </div>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 mb-2">
                {DAYS.map(d => (
                  <div key={d} className="text-xs font-medium text-muted-foreground text-center py-1">{d}</div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {calDays.map((day, idx) => {
                  const key = day ? `${calYear}-${calMonth}-${day}` : `empty-${idx}`;
                  const dayTasks = day ? (calendarTasks[key] || []) : [];
                  const isToday = day && new Date().getDate() === day && new Date().getMonth() === calMonth && new Date().getFullYear() === calYear;
                  return (
                    <div
                      key={key}
                      className={`min-h-[80px] border border-border/30 p-1 ${
                        day ? "bg-card" : "bg-muted/30"
                      } ${isToday ? "ring-2 ring-primary/30 rounded" : ""}`}
                    >
                      {day && (
                        <>
                          <span className={`text-xs font-medium ${isToday ? "text-primary font-medium" : "text-muted-foreground"}`}>
                            {day}
                          </span>
                          <div className="space-y-0.5 mt-0.5">
                            {dayTasks.slice(0, 2).map((t, ti) => (
                              <div key={ti} className={`text-[10px] px-1 py-0.5 rounded truncate ${t.color}`}>
                                {t.label}
                              </div>
                            ))}
                            {dayTasks.length > 2 && (
                              <div className="text-[10px] text-muted-foreground px-1">+{dayTasks.length - 2} more</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR: Notes overview & Team */}
          <div className="space-y-6">
            {/* Team members management */}
            <div className="bg-card rounded-xl border border-border p-5">
              <h3 className="font-heading text-sm font-medium text-foreground flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-primary" /> Team Members
              </h3>
              
              {teamMembers.length === 0 && !showAddMember ? (
                <p className="text-xs text-muted-foreground mb-3">No team members yet. Add your team to assign tasks.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {teamMembers.map(member => {
                    const memberTasks = assignments.filter(a => a.assignee === member.name);
                    const done = memberTasks.filter(a => a.status === "done").length;
                    return (
                      <div key={member.id} className="flex items-center justify-between group">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                            {member.name[0]?.toUpperCase()}
                          </div>
                          <span className="text-sm text-foreground">{member.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {memberTasks.length > 0 && (
                            <span className="text-xs text-muted-foreground">{done}/{memberTasks.length}</span>
                          )}
                          <button
                            onClick={() => removeTeamMember(member.id)}
                            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {showAddMember ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMemberName}
                    onChange={e => setNewMemberName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addTeamMember()}
                    placeholder="Name..."
                    className="flex-1 px-3 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
                    autoFocus
                  />
                  <Button size="sm" onClick={addTeamMember} disabled={!newMemberName.trim()}>Add</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowAddMember(false); setNewMemberName(""); }}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs gap-1"
                  onClick={() => setShowAddMember(true)}
                >
                  <Plus className="w-3 h-3" /> Add Team Member
                </Button>
              )}
            </div>

            {/* All notes overview */}
            <div className="bg-card rounded-xl border border-border p-5">
              <h3 className="font-heading text-sm font-medium text-foreground flex items-center gap-2 mb-4">
                <StickyNote className="w-4 h-4 text-primary" /> All Notes
              </h3>
              {notes.length === 0 ? (
                <p className="text-xs text-muted-foreground">No notes yet. Open a timeline step to add notes.</p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {notes.map(n => (
                    <div key={n.id} className="border-l-2 border-primary/30 pl-3">
                      <p className="text-xs font-medium text-primary">{results.timelineSteps[n.stepIndex]?.step}</p>
                      <p className="text-sm text-foreground mt-0.5">{n.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assign modal */}
      <AnimatePresence>
        {assignModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={() => setAssignModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-xl border border-border p-6 w-80 shadow-lg"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-heading text-sm font-medium text-foreground mb-4">Assign to team member</h3>
              {teamMembers.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-xs text-muted-foreground mb-3">No team members yet. Add members in the sidebar first.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {teamMembers.map(member => (
                    <button
                      key={member.id}
                      onClick={() => assignTask(member.name)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                        {member.name[0]?.toUpperCase()}
                      </div>
                      <span className="text-sm text-foreground">{member.name}</span>
                    </button>
                  ))}
                </div>
              )}
              <Button variant="ghost" size="sm" className="w-full mt-3" onClick={() => setAssignModal(null)}>
                Cancel
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
